/**
 * 神奈川県の背景ツアー「神奈川めぐり」。10か所、それぞれに3つの豆知識。
 *
 * 書いてはいけないこと。
 * - 鎌倉大仏は公式が「商業利用の写真を撮影される方は、事前に許可を受ける必要があります」と
 *   明記しているので入れていない。
 * - 小田原城の天守閣は昭和35年の復興建築で権利の保護期間内の可能性があるため入れていない。
 * - 横浜赤レンガ倉庫はいま文化・商業施設として運営されているので入れていない。
 * - 城ヶ島灯台は2019年にトリックアートのラッピングが施されているので入れていない。
 *   城ヶ島公園の安房埼灯台も2020年の建て替えなので描かない。水仙とウミウと海だけにする。
 * - 芦ノ湖には箱根神社の水中鳥居があるので、湖を描くときは鳥居の入らない構図にする。
 * - 大山は阿夫利神社の境内に鳥居があるので、山そのものを遠景で描く。
 * - 仙石原のススキは公式ページを開けなかったので、ススキとは書かない。
 * - 大涌谷はいまも火山性ガスが出ていて立入規制がある現役の噴気地帯である。そう書く。
 * - 報国寺は永享10年の永享の乱で足利義久がこの地で自害したと公式が記している。竹の庭だけを描く。
 */

import type { StageTour } from '../types';

export const kanagawaSceneryTour: StageTour = {
  scenes: [
    {
      id: 'sankeien',
      name: '三溪園',
      area: '横浜市中区',
      image: '/assets/scenery/kanagawa/scenes/01_sankeien.webp',
      hotspots: [
        {
          id: 'sankeien-opened',
          x: 0.25,
          y: 0.65,
          title: '1906年に無料で開園',
          body: '1906年（明治39年）5月1日に、現在の外苑部分が無料で開園した。作ったのは実業家で茶人の原三溪。生糸貿易の実業家で「横浜の恩人」と呼ばれた。',
          source: 'https://www.sankeien.or.jp/history/',
          mapQuery: '三溪園',
        },
        {
          id: 'sankeien-meisho',
          x: 0.55,
          y: 0.4,
          title: '2007年に国の名勝',
          body: '2007年（平成19年）に国の名勝に指定された。',
          source: 'https://www.sankeien.or.jp/history/',
          mapQuery: '横浜市中区 三溪園',
        },
        {
          id: 'sankeien-buildings',
          x: 0.8,
          y: 0.28,
          title: '17棟の建築',
          body: '国の重要文化財の建造物10棟、横浜市指定有形文化財の建造物3棟を含め、17棟の建築物をもつ。丘の上の三重塔は、1914年（大正3年）に京都相楽郡加茂の燈明寺から移築されたもの。',
          source: 'https://www.sankeien.or.jp/history/',
          mapQuery: '三溪園 旧燈明寺三重塔',
        },
      ],
    },
    {
      id: 'hokokuji',
      name: '報国寺 竹の庭',
      area: '鎌倉市',
      image: '/assets/scenery/kanagawa/scenes/02_hokokuji.webp',
      hotspots: [
        {
          id: 'hokokuji-founded',
          x: 0.25,
          y: 0.35,
          title: '建武元年の創建',
          body: '建武元年（1334年）に創建された臨済宗建長寺派の寺院。開山は仏乗禅師天岸慧広、開基は足利家時で、家時を弔うために上杉重兼によって創建された。',
          source: 'https://houkokuji.or.jp/about/',
          mapQuery: '報国寺 鎌倉市',
        },
        {
          id: 'hokokuji-bamboo',
          x: 0.55,
          y: 0.6,
          title: '見え方が刻々と変わる',
          body: '竹は季節や天気、時間帯によって見え方が変化する。竹の間に差し込む日差し、雨の日に滴る雨水、風で揺らいで軋む音など、さまざまな姿が楽しめる。',
          source: 'https://houkokuji.or.jp/about/',
          mapQuery: '報国寺 竹の庭',
        },
        {
          id: 'hokokuji-history',
          x: 0.8,
          y: 0.75,
          title: '鎌倉文士が住んだ谷戸',
          body: '寺のある谷戸は、宅間派と呼ばれる芸術家集団が拠点にしたことから宅間谷と呼ばれ、近代には川端康成や林房雄といった鎌倉文士がこの地に住んでいた。',
          source: 'https://houkokuji.or.jp/about/',
          mapQuery: '鎌倉 宅間谷',
        },
      ],
    },
    {
      id: 'umanose-domon',
      name: '馬の背洞門',
      area: '三浦市',
      image: '/assets/scenery/kanagawa/scenes/03_umanose.webp',
      hotspots: [
        {
          id: 'umanose-domon-origin',
          x: 0.28,
          y: 0.35,
          title: '波と風雨が彫った穴',
          body: '自然が作った海蝕洞穴で、長い年月をかけて波浪や風雨などに浸蝕されてこのような見事な形になった。',
          source:
            'https://www.city.miura.kanagawa.jp/soshiki/kankoshokoka/kankoshokoka_kanko/kankoukaisetsuban/kankokaisetsuban_ippan/9983.html',
          mapQuery: '馬の背洞門',
        },
        {
          id: 'umanose-domon-size',
          x: 0.55,
          y: 0.55,
          title: '高さ8メートル',
          body: '大きさは高さ八メートル、横六メートル、厚さ二メートル。',
          source:
            'https://www.city.miura.kanagawa.jp/soshiki/kankoshokoka/kankoshokoka_kanko/kankoukaisetsuban/kankokaisetsuban_ippan/9983.html',
          mapQuery: '城ヶ島 馬の背洞門',
        },
        {
          id: 'umanose-domon-names',
          x: 0.8,
          y: 0.78,
          title: '呼び名がいくつもある',
          body: '地層は第三紀層、鮮新統、三浦層群に属し、土質は凝灰質砂礫岩という軟らかい岩質。土地の人は「馬の背の洞門」のほか「めぐりの洞門」「眼鏡の洞門」などと呼ぶ。どの名も洞門の形から名づけられたもの。',
          source:
            'https://www.city.miura.kanagawa.jp/soshiki/kankoshokoka/kankoshokoka_kanko/kankoukaisetsuban/kankokaisetsuban_ippan/9983.html',
          mapQuery: '城ヶ島',
        },
      ],
    },
    {
      id: 'owakudani',
      name: '大涌谷',
      area: '箱根町',
      image: '/assets/scenery/kanagawa/scenes/04_owakudani.webp',
      hotspots: [
        {
          id: 'owakudani-active',
          x: 0.28,
          y: 0.35,
          title: '活火山の噴煙地',
          body: '大涌谷は、活火山である箱根山の噴煙地である。自然研究路は延長約700メートルの散策路。',
          source: 'https://www.pref.kanagawa.jp/docs/f4y/hakone/fujihakoneizu/oowakudani.html',
          mapQuery: '大涌谷',
        },
        {
          id: 'owakudani-gas',
          x: 0.58,
          y: 0.6,
          title: 'いまも火山性ガスが出ている',
          body: '人体に有害な火山性ガス（硫化水素、二酸化硫黄）が発生しており、24時間の観測が行われている。平成27年の火山活動以来の立入規制があり、令和4年3月28日から監視員が引率する方式で再開された。',
          source: 'https://www.pref.kanagawa.jp/docs/f4y/hakone/fujihakoneizu/oowakudani.html',
          mapQuery: '大涌谷 箱根町',
        },
        {
          id: 'owakudani-origin',
          x: 0.8,
          y: 0.8,
          title: '3千年前の水蒸気爆発の名残',
          body: '約3千年前、神山の北西斜面で起きた水蒸気爆発の噴火活動の名残が現在の大涌谷である。',
          source: 'https://www.town.hakone.kanagawa.jp/www/contents/1100000001225/index.html',
          mapQuery: '箱根山 大涌谷',
        },
      ],
    },
    {
      id: 'chuo-kakokyu',
      name: '箱根の中央火口丘',
      area: '箱根町',
      image: '/assets/scenery/kanagawa/scenes/05_sengokuhara.webp',
      hotspots: [
        {
          id: 'chuo-kakokyu-age',
          x: 0.28,
          y: 0.35,
          title: '50万年前からの火山',
          body: '箱根火山は今からおよそ50万年前に活動をはじめた、比較的古い火山である。',
          source: 'https://www.town.hakone.kanagawa.jp/www/contents/1100000001225/index.html',
          mapQuery: '箱根 仙石原',
        },
        {
          id: 'chuo-kakokyu-domes',
          x: 0.58,
          y: 0.4,
          title: '溶岩ドームの形成と崩壊',
          body: '5万年前ごろからは、現在の神山を中心とする中央火口丘で活動が始まり、約3万年前ごろからは小塚山、台ヶ岳、二子山などの溶岩ドームの形成と、ドームの崩壊による火砕流の発生がくり返された。',
          source: 'https://www.town.hakone.kanagawa.jp/www/contents/1100000001225/index.html',
          mapQuery: '神山 箱根町',
        },
        {
          id: 'chuo-kakokyu-made',
          x: 0.8,
          y: 0.75,
          title: '6.6キロの探勝歩道',
          body: '仙石原湖尻自然探勝歩道は、湖尻水門を出発し、外輪山の内側の麓を通って仙石原に抜ける6.6キロメートルのルート。早川や中央火口丘の神山、冠ヶ岳などが見られる。',
          source: 'https://www.pref.kanagawa.jp/docs/f4y/hakone/fujihakoneizu/tanshouhodou.html',
          mapQuery: '仙石原湖尻自然探勝歩道',
        },
      ],
    },
    {
      id: 'onshi-hakone',
      name: '恩賜箱根公園',
      area: '箱根町',
      image: '/assets/scenery/kanagawa/scenes/06_onshi_hakone.webp',
      hotspots: [
        {
          id: 'onshi-hakone-rikyu',
          x: 0.28,
          y: 0.65,
          title: 'もとは箱根離宮',
          body: 'かつて箱根離宮があった地で、昭和34年4月28日に公園として設置された。開設面積は15.9ヘクタール。',
          source: 'https://www.pref.kanagawa.jp/docs/tu5/cnt/f6599/p19701.html',
          mapQuery: '恩賜箱根公園',
        },
        {
          id: 'onshi-hakone-registered',
          x: 0.55,
          y: 0.45,
          title: '平成25年に国登録記念物',
          body: '平成25年8月に、国登録記念物（名勝地関係）として登録された。',
          source: 'https://www.pref.kanagawa.jp/docs/tu5/cnt/f6599/p19701.html',
          mapQuery: '箱根町 恩賜公園',
        },
        {
          id: 'onshi-hakone-view',
          x: 0.78,
          y: 0.3,
          title: '標高724メートルのカルデラ湖',
          body: '芦ノ湖は箱根火山の中にあるカルデラ湖で、標高724メートルに位置し、周囲19キロ、面積6.9平方キロ、最深部は43.5メートルある。',
          source: 'https://www.town.hakone.kanagawa.jp/www/contents/1100000001498/index.html',
          mapQuery: '芦ノ湖',
        },
      ],
    },
    {
      id: 'miyagase',
      name: '宮ヶ瀬ダム',
      area: '相模原市',
      image: '/assets/scenery/kanagawa/scenes/07_miyagase.webp',
      hotspots: [
        {
          id: 'miyagase-largest',
          x: 0.3,
          y: 0.3,
          title: '首都圏最大のダム',
          body: '国土交通省は宮ヶ瀬ダムを「首都圏最大のダム」と記している。',
          source: 'https://www.ktr.mlit.go.jp/sagami/sagami00035.html',
          mapQuery: '宮ヶ瀬ダム',
        },
        {
          id: 'miyagase-height',
          x: 0.58,
          y: 0.6,
          title: '高さ156メートル',
          body: '156メートルという高さは、ヨコハマランドマークタワーのほぼ半分に相当する。',
          source: 'https://www.ktr.mlit.go.jp/sagami/sagami00035.html',
          mapQuery: '宮ヶ瀬ダム 愛川町',
        },
        {
          id: 'miyagase-lake',
          x: 0.8,
          y: 0.8,
          title: '湖面は東京ドーム100個分',
          body: 'ダム湖の湖面の広さは約4.6平方キロメートルで、東京ドーム100個分にあたる。約2億立方メートルという総貯水量は、芦ノ湖の水量とほぼ同じ。',
          source: 'https://www.ktr.mlit.go.jp/sagami/sagami00035.html',
          mapQuery: '宮ヶ瀬湖',
        },
      ],
    },
    {
      id: 'jogashima-park',
      name: '県立城ヶ島公園',
      area: '三浦市',
      image: '/assets/scenery/kanagawa/scenes/08_jogashima.webp',
      hotspots: [
        {
          id: 'jogashima-park-park',
          x: 0.28,
          y: 0.35,
          title: '昭和33年に設置',
          body: '県立城ヶ島公園は昭和33年12月16日に設置された。面積は14.6ヘクタール。',
          source: 'https://www.pref.kanagawa.jp/docs/wk7/cnt/f11539/p35617.html',
          mapQuery: '県立城ヶ島公園',
        },
        {
          id: 'jogashima-park-narcissus',
          x: 0.55,
          y: 0.65,
          title: '30万株の八重咲水仙',
          body: '毎年1月中旬頃から2月中旬頃にかけて、30万株の八重咲水仙が、ふくよかな甘い香りとともに清らかな花を咲かせる。',
          source: 'https://www.pref.kanagawa.jp/docs/wk7/cnt/f11539/p35617.html',
          mapQuery: '城ヶ島公園 水仙',
        },
        {
          id: 'jogashima-park-umiu',
          x: 0.8,
          y: 0.78,
          title: 'ウミウは11月に飛来する',
          body: '県指定天然記念物である渡り鳥ウミウの生息地でもある。ウミウは千島列島から11月頃に飛来し、翌年の4月頃まで、切り立った崖で過ごす。',
          source: 'https://www.pref.kanagawa.jp/docs/d2t/chiki/jyogashimaosusume.html',
          mapQuery: '城ヶ島 ウミウ展望台',
        },
      ],
    },
    {
      id: 'oyama',
      name: '大山',
      area: '伊勢原市',
      image: '/assets/scenery/kanagawa/scenes/09_oyama.webp',
      hotspots: [
        {
          id: 'oyama-height',
          x: 0.5,
          y: 0.32,
          title: '標高1252メートルの霊山',
          body: '大山の標高は1,252メートル。縄文の昔から霊山として信仰の対象とされてきた。',
          source:
            'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/35_2_kanagawa.html',
          mapQuery: '大山 伊勢原市',
        },
        {
          id: 'oyama-shrine',
          x: 0.25,
          y: 0.7,
          title: '二千二百余年前から',
          body: '山腹の大山阿夫利神社は、人皇第十代崇神天皇の御代、今から二千二百余年以前に創建されたと伝えられている。祭神は大山祗大神、高おかみ神、大雷神。',
          source: 'https://www.afuri.or.jp/about/',
          mapQuery: '大山阿夫利神社',
        },
        {
          id: 'oyama-mairi',
          x: 0.78,
          y: 0.72,
          title: '大山詣りは日本遺産',
          body: '平成28年4月に「大山詣り」が日本遺産に認定された。江戸期には年間で数十万の人が訪れたと記録されている。',
          source: 'https://www.afuri.or.jp/about/',
          mapQuery: '大山ケーブル',
        },
      ],
    },
    {
      id: 'sarushima',
      name: '猿島 砲台跡',
      area: '横須賀市',
      image: '/assets/scenery/kanagawa/scenes/10_sarushima.webp',
      hotspots: [
        {
          id: 'sarushima-shiseki',
          x: 0.28,
          y: 0.35,
          title: '軍事遺跡として初の国史跡',
          body: '国史跡「東京湾要塞跡」として平成27年3月10日に指定された。猿島砲台跡と千代ヶ崎砲台跡の2つが史跡に指定されており、軍事遺跡としては日本で初めての国史跡になる。',
          source: 'https://www.city.yokosuka.kanagawa.jp/8120/bunkazai/tokyowan.html',
          mapQuery: '猿島 横須賀市',
        },
        {
          id: 'sarushima-built',
          x: 0.55,
          y: 0.6,
          title: '明治14年起工、17年竣工',
          body: '明治14年（1881年）11月5日に起工し、明治17年（1884年）6月30日に竣工した。東京湾内湾への敵艦船の侵入を阻み、横須賀・長浦の両軍港を守る役割を担った。',
          source: 'https://www.city.yokosuka.kanagawa.jp/8120/bunkazai/kuni15.html',
          mapQuery: '猿島砲台跡',
        },
        {
          id: 'sarushima-brick',
          x: 0.8,
          y: 0.78,
          title: 'フランス積の煉瓦',
          body: '弾薬庫などの煉瓦建造物に採用された組積法はフランス積（フランドル積）で、後に増設された部分にはオランダ積が採用された。',
          source: 'https://www.city.yokosuka.kanagawa.jp/8120/bunkazai/kuni15.html',
          mapQuery: '猿島',
        },
      ],
    },
  ],
};
